import { useCallback, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { renderEmailTemplate } from "src/services/CampaignAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import type { EmailDesign, GeneratedTemplate } from "../../AiEmail/AiCampaignTypes";
import { parseGeneratedDesign } from "../../AiEmail/AiCampaignUtils";
import { createHtmlBlockDesign } from "../../AiEmail/EmailEditorUtils";
import { TEMPLATE_EDITOR_KEY } from "../TemplateEditorConstants";

interface TemplatePreview {
  templateId: string;
  html: string;
}

export const getPreviousCampaignTemplate = (emailHtml?: string): GeneratedTemplate | null =>
  emailHtml?.trim() ? { design: createHtmlBlockDesign(emailHtml), html: emailHtml } : null;

interface UseTemplateSelectionParams {
  campaignName: string;
  initialTemplate: GeneratedTemplate | null;
  onApplied: () => void;
}

/** Template preview, render and selection shared by the email and chat template wizards. */
export const useTemplateSelection = ({
  campaignName,
  initialTemplate,
  onApplied,
}: UseTemplateSelectionParams) => {
  const { t, i18n } = useTranslation();
  const [template, setTemplate] = useState<GeneratedTemplate | null>(initialTemplate);
  const [renderingTemplateId, setRenderingTemplateId] = useState<string | null>(null);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const previewRequestRef = useRef<string | null>(null);
  const trimmedCampaignName = campaignName.trim();

  const notifyRenderError = useCallback(
    (error: unknown) =>
      message.error(
        getThrownApiDetail(error) || t(`${TEMPLATE_EDITOR_KEY}.errors.renderTemplateFailed`)
      ),
    [t]
  );

  const openPreview = useCallback(
    async (templateId: string) => {
      previewRequestRef.current = templateId;
      setPreview({ templateId, html: "" });
      try {
        const response = await renderEmailTemplate(
          { template_id: templateId },
          getApiLangFromI18n(i18n)
        );
        if (previewRequestRef.current === templateId) {
          setPreview({ templateId, html: response?.html ?? "" });
        }
      } catch (error) {
        if (previewRequestRef.current === templateId) {
          previewRequestRef.current = null;
          setPreview(null);
          notifyRenderError(error);
        }
      }
    },
    [i18n, notifyRenderError]
  );

  const closePreview = useCallback(() => {
    previewRequestRef.current = null;
    setPreview(null);
  }, []);

  const applyTemplate = useCallback(
    async (templateId: string): Promise<boolean> => {
      setRenderingTemplateId(templateId);
      try {
        const response = await renderEmailTemplate(
          { template_id: templateId, campaign_name: trimmedCampaignName || undefined },
          getApiLangFromI18n(i18n)
        );
        setTemplate({
          design: parseGeneratedDesign(response?.template),
          html: response?.html ?? "",
        });
        closePreview();
        onApplied();
        return true;
      } catch (error) {
        notifyRenderError(error);
        return false;
      } finally {
        setRenderingTemplateId(null);
      }
    },
    [closePreview, i18n, notifyRenderError, onApplied, trimmedCampaignName]
  );

  const saveTemplateDesign = useCallback((design: EmailDesign) => {
    setTemplate(current => (current ? { ...current, design } : current));
  }, []);

  const resetSelection = useCallback((nextTemplate: GeneratedTemplate | null) => {
    setTemplate(nextTemplate);
    setRenderingTemplateId(null);
    setPreview(null);
  }, []);

  return {
    template,
    preview,
    renderingTemplateId,
    openPreview,
    closePreview,
    applyTemplate,
    saveTemplateDesign,
    resetSelection,
  };
};
